import { reactive, readonly } from 'vue';
import { ErroRecente } from '@/models/assistente/sofia.model';
import { extrairMensagemErroHttp } from '@/services/helper/erro-http.util';

/**
 * Registro circular dos ÚLTIMOS erros de requisição desta sessão, para a Sofia do suporte.
 *
 * Alimentado pelos interceptors de resposta (`setupInterceptors.ts`) com uma linha só, sem mudar
 * nada do tratamento que já existe (toasts, renovação de token, erro de campo).
 *
 * PRIVACIDADE — o que entra e o que NUNCA entra:
 *  - entra: método, caminho da URL SEM query string (a query costuma carregar CPF, nome, código
 *    de estudante), status, a mensagem do corpo da RESPOSTA truncada e o `traceId` quando o
 *    servidor devolve um;
 *  - nunca entra: corpo da requisição, cabeçalhos, token, cookie.
 *
 * Fica só em memória (some no F5) e só é ENVIADO quando quem pergunta é suporte/desenvolvimento
 * (`sessionService.IsUsuarioSuporteOuDesenvolvimento()`); o servidor também descarta para o
 * perfil usuário.
 */

const LIMITE = 5;
const TAMANHO_MAXIMO_MENSAGEM = 300;

const estado = reactive({ erros: [] as ErroRecente[] });

/** Caminho da URL sem origem e sem query string. Nunca lança. */
function caminhoSemQuery(url: string | undefined, baseURL: string | undefined): string {
  const bruta = url ?? '';
  try {
    const absoluta = /^https?:\/\//i.test(bruta)
      ? bruta
      : `${(baseURL ?? '').replace(/\/+$/, '')}/${bruta.replace(/^\/+/, '')}`;
    return new URL(absoluta, window.location.origin).pathname;
  } catch {
    return bruta.split('?')[0].split('#')[0];
  }
}

function truncar(texto: string): string {
  const limpo = (texto ?? '').replace(/\s+/g, ' ').trim();
  return limpo.length > TAMANHO_MAXIMO_MENSAGEM
    ? `${limpo.slice(0, TAMANHO_MAXIMO_MENSAGEM - 1)}…`
    : limpo;
}

class RegistroErrosService {
  readonly estado = readonly(estado);

  /**
   * Registra um erro do axios. Qualquer falha aqui é engolida: registrar o erro nunca pode
   * atrapalhar o tratamento do erro em si.
   */
  registrarErroAxios(err: any) {
    try {
      const config = err?.config ?? {};
      const resposta = err?.response;
      const corpo = resposta?.data;

      const traceId =
        corpo && typeof corpo === 'object' && typeof corpo.traceId === 'string'
          ? corpo.traceId
          : undefined;

      const mensagem = resposta
        ? extrairMensagemErroHttp(err) || `Erro HTTP ${resposta.status}`
        : 'Sem resposta do servidor (falha de rede ou tempo esgotado).';

      const erro: ErroRecente = {
        quando: new Date().toISOString(),
        metodo: String(config.method ?? 'GET').toUpperCase(),
        url: caminhoSemQuery(config.url, config.baseURL),
        status: resposta?.status ?? 0,
        mensagem: truncar(mensagem),
        ...(traceId ? { traceId } : {}),
      };

      estado.erros.push(erro);
      if (estado.erros.length > LIMITE) estado.erros.splice(0, estado.erros.length - LIMITE);
    } catch {
      /* nunca atrapalhar o interceptor */
    }
  }

  /** Cópia simples (sem proxy reativo), do mais antigo para o mais recente. */
  listar(): ErroRecente[] {
    return estado.erros.map((e) => ({ ...e }));
  }

  ultimo(): ErroRecente | null {
    return estado.erros.length ? { ...estado.erros[estado.erros.length - 1] } : null;
  }

  limpar() {
    estado.erros.splice(0);
  }
}

export default new RegistroErrosService();
